import { useState, useCallback, useRef } from 'react'

interface CachedSession {
  sessionId: string
  messages: any[]
  lastAccessed: number
  isLoaded: boolean
}

/**
 * 会话缓存管理Hook
 * 提供会话数据的本地缓存，避免重复请求
 */
export const useSessionCache = () => {
  const [cacheSize, setCacheSize] = useState(0)
  const cacheRef = useRef<Map<string, CachedSession>>(new Map())

  // 检查会话是否已缓存
  const isSessionCached = useCallback((sessionId: string) => {
    const cached = cacheRef.current.get(sessionId)
    return cached?.isLoaded || false
  }, [])

  // 缓存会话数据
  const cacheSession = useCallback((sessionId: string, messages: any[]) => {
    cacheRef.current.set(sessionId, {
      sessionId,
      messages,
      lastAccessed: Date.now(),
      isLoaded: true,
    })
    setCacheSize(cacheRef.current.size)
  }, [])

  // 获取缓存的会话数据
  const getCachedSession = useCallback((sessionId: string) => {
    const cached = cacheRef.current.get(sessionId)
    if (cached) {
      return cached.messages
    }
    return null
  }, [])

  // 更新会话数据
  const updateSession = useCallback((sessionId: string, messages: any[]) => {
    cacheRef.current.set(sessionId, {
      sessionId,
      messages,
      lastAccessed: Date.now(),
      isLoaded: true,
    })
    setCacheSize(cacheRef.current.size)
  }, [])

  // 更新会话访问时间
  const updateSessionAccess = useCallback((sessionId: string) => {
    const cached = cacheRef.current.get(sessionId)
    if (cached) {
      cacheRef.current.set(sessionId, {
        ...cached,
        lastAccessed: Date.now(),
      })
    }
  }, [])

  // 清除过期缓存（超过30分钟）
  const clearExpiredCache = useCallback(() => {
    const now = Date.now()
    const thirtyMinutes = 30 * 60 * 1000

    for (const [sessionId, session] of cacheRef.current) {
      if (now - session.lastAccessed >= thirtyMinutes) {
        cacheRef.current.delete(sessionId)
      }
    }
    setCacheSize(cacheRef.current.size)
  }, [])

  // 清除特定会话缓存
  const clearSessionCache = useCallback((sessionId: string) => {
    cacheRef.current.delete(sessionId)
    setCacheSize(cacheRef.current.size)
  }, [])

  // 清除所有缓存
  const clearAllCache = useCallback(() => {
    cacheRef.current.clear()
    setCacheSize(0)
  }, [])

  return {
    isSessionCached,
    cacheSession,
    getCachedSession,
    updateSession,
    updateSessionAccess,
    clearExpiredCache,
    clearSessionCache,
    clearAllCache,
    cacheSize,
  }
}
